import { reasoningEffortForThinking } from "@perceptron/api/apiContracts/chatCompletions";
import { strings } from "@perceptron/constants";
import { downscaleAndConvertImage } from "@perceptron/core";
import {
  Annotation,
  AnnotationSource,
  AnnotationType,
  BoxAnnotation,
  CollectionAnnotation,
  isCollectionAnnotation,
  PointAnnotation,
} from "@perceptron/core/types";
import { generateUUID } from "@perceptron/utils/crypto";
import { clamp, COORDINATE_SPACE } from "@perceptron/utils/math";
import { toast } from "sonner";

import { DetectTemplate, getTemplateAssetCdnUrl } from "@assets/templates";
import { env } from "@demo/env";
import { trackDetect, trackMedia } from "@demo/libs/analytics/utils";
import { demoDetectApi } from "@demo/libs/api/demoApiClient";
import { MediaUploadService } from "@demo/libs/media-upload";
import { saveDetectRunPreferences } from "@demo/libs/run-preferences";
import { Detection, DetectRequest } from "@demo/server/api/contracts/detect";
import { useDetectStore } from "@demo/store/detect";
import { useMediaCarouselStore } from "@demo/store/media-carousel";
import { useModelStore } from "@demo/store/model";
import useSessionStore from "@demo/store/session";

/** The in-flight detect request's abort controller, so {@link stopDetect} can cancel it. */
let activeDetectAbort: AbortController | null = null;

/** HTTP status off a ky `HTTPError` (duck-typed — ky is not a direct dependency). */
function errorStatusCode(error: unknown): number | undefined {
  return error instanceof Error && "response" in error && error.response instanceof Response
    ? error.response.status
    : undefined;
}

/** Clear detect results from the shared annotation store. */
export function clearDetections(): void {
  const model = useModelStore.getState();
  model.setAnnotations([]);
  model.setClickedAnnotationId(undefined, undefined);
  model.setHighlightedAnnotationIds(undefined, undefined);
}

/**
 * Remove a user category together with its detections. Removing the category
 * alone would leave its boxes in the annotation store, which `CategoryList`
 * would then re-surface as a model-generated result card.
 */
export function removeDetectCategory(label: string): void {
  useDetectStore.getState().removeCategory(label);
  const model = useModelStore.getState();
  model.setAnnotations(
    model.annotations.filter((a) => !(isCollectionAnnotation(a) && a.label.toLowerCase() === label.toLowerCase())),
  );
}

/**
 * Reset the image only: clear the uploaded image, its detections, and the run
 * state, while keeping the user's categories. Backs the image view's reset.
 */
export function resetDetectImage(): void {
  const detect = useDetectStore.getState();
  detect.setImage(undefined);
  detect.setStatus("idle");
  detect.setRequestedCategories([]);
  clearDetections();
  useMediaCarouselStore.getState().clearMediaInAgentCarousel();
}

/**
 * Wipe every category and its detections, keeping the uploaded image. Backs the
 * Classes panel's "Clear all".
 */
export function clearDetectCategories(): void {
  const detect = useDetectStore.getState();
  detect.clearCategories();
  detect.setStatus("idle");
  detect.setRequestedCategories([]);
  clearDetections();
}

/** Full reset — image, categories, and detections. Used when leaving detect. */
export function resetDetectAll(): void {
  useDetectStore.getState().resetDetect();
  clearDetections();
  useMediaCarouselStore.getState().clearMediaInAgentCarousel();
}

/** Natural pixel dimensions of an image File (used to normalize box coords). */
async function loadImageDimensions(file: File): Promise<{ width: number; height: number }> {
  const bitmap = await createImageBitmap(file);
  const { width, height } = bitmap;
  bitmap.close();
  return { width, height };
}

/**
 * Downscale, upload, and ingest an image for detect mode: store it (S3 download
 * URL + natural dims, used to normalize box coords) and show it in the shared
 * media carousel. Mirrors Compose's upload — the detect request passes an image
 * URL, not a base64 payload. Clears any prior detections.
 */
export async function addDetectImage(file: File): Promise<void> {
  const downscaled = await downscaleAndConvertImage(file, {
    patchSize: env.NEXT_PUBLIC_IMAGE_DOWNSCALE_PATCH_SIZE,
    maxNumPatches: env.NEXT_PUBLIC_IMAGE_DOWNSCALE_MAX_NUM_PATCHES,
    epsilon: env.NEXT_PUBLIC_IMAGE_DOWNSCALE_EPSILON,
    pixelShuffleScale: env.NEXT_PUBLIC_IMAGE_DOWNSCALE_PIXEL_SHUFFLE_SCALE,
    resultQuality: env.NEXT_PUBLIC_IMAGE_DOWNSCALE_RESULT_QUALITY,
  });

  const { width, height } = await loadImageDimensions(downscaled);
  const { url, key, extension } = await MediaUploadService.uploadFile(downscaled);

  trackMedia.upload("image", file.size, file.name);

  const detect = useDetectStore.getState();
  detect.setImage({ key, url, width, height });
  detect.setStatus("idle");

  // Clear prior detections and show the new image in the shared media carousel.
  clearDetections();
  useMediaCarouselStore.getState().setMediaInAgentCarousel([{ data: "", extension, key, url }]);
}

/** Natural dimensions of an image at a URL — loads the element only (no upload). */
async function loadImageDimensionsFromUrl(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

/**
 * Pre-fill the detect surface from a detect example: full-reset, apply the
 * example's categories + config (synchronously), then point the surface at the
 * example image. The image is a template asset that's already hosted and
 * resolvable by the detect proxy (`resolveMediaAssetUrl` is template-aware), so
 * we reference it by UUID rather than re-uploading it — `runDetect` sends the
 * UUID as `image_key` and the backend resolves it, mirroring how Compose applies
 * a template.
 */
export async function applyDetectExample(template: DetectTemplate): Promise<void> {
  const prefill = template.detect;

  resetDetectAll();
  const detect = useDetectStore.getState();
  for (const category of prefill.categories ?? []) {
    detect.addCategory(category);
  }
  detect.setOutputType(prefill.config?.output ?? "box");
  detect.setEnableThinking(prefill.config?.enableThinking ?? false);

  const asset = template.assets.find((a) => a.uuid === prefill.mediaUuid);
  if (!asset) {
    console.error(`applyDetectExample: media asset ${prefill.mediaUuid} is missing from template.assets`);
    return;
  }

  const url = getTemplateAssetCdnUrl(prefill.mediaUuid);
  try {
    // Natural dims (for box-coord normalization) come from the asset when authored, else the image.
    const { width, height } =
      asset.width && asset.height
        ? { width: asset.width, height: asset.height }
        : await loadImageDimensionsFromUrl(url);

    detect.setImage({ key: prefill.mediaUuid, url, width, height });
    useMediaCarouselStore
      .getState()
      .setMediaInAgentCarousel([{ data: "", extension: asset.extension, key: prefill.mediaUuid, url }]);
  } catch (error) {
    console.error("applyDetectExample: failed to load example image", error);
    toast.error(strings.mediaCarousel.errors.imageFailedToLoad);
  }
}

/** A single detection geometry the UI can render: a box or a point. */
type DetectionAnnotation = BoxAnnotation | PointAnnotation;

/**
 * Convert pixel-space detections into normalized (0–1000) annotations, grouped
 * into one collection per category `mention`. Each detection becomes a box
 * (`point_box` output) or a point (`point` output); detections with neither are
 * skipped. Geometries are numbered globally ("Box 1"/"Point 2", … in detection
 * order) so chips line up across categories.
 */
export function detectionsToAnnotations(
  detections: Detection[],
  imageWidth: number,
  imageHeight: number,
): Annotation[] {
  const toNorm = (value: number, dimension: number) =>
    clamp(dimension > 0 ? (value / dimension) * COORDINATE_SPACE : 0, 0, COORDINATE_SPACE);

  const groups = new Map<string, DetectionAnnotation[]>();
  let count = 0;

  for (const detection of detections) {
    let annotation: DetectionAnnotation | null = null;

    if (detection.point_box) {
      count += 1;
      annotation = {
        id: `content-${count}`,
        type: AnnotationType.BOX,
        // Detect is single-image by design, so every detection is asset 0.
        assetIdx: 0,
        label: `Box ${count}`,
        source: AnnotationSource.CONTENT,
        coordinates: {
          top_left: {
            x: toNorm(detection.point_box.top_left.x, imageWidth),
            y: toNorm(detection.point_box.top_left.y, imageHeight),
          },
          bottom_right: {
            x: toNorm(detection.point_box.bottom_right.x, imageWidth),
            y: toNorm(detection.point_box.bottom_right.y, imageHeight),
          },
        },
      };
    } else if (detection.point) {
      count += 1;
      annotation = {
        id: `content-${count}`,
        type: AnnotationType.POINT,
        assetIdx: 0,
        label: `Point ${count}`,
        source: AnnotationSource.CONTENT,
        coordinates: {
          x: toNorm(detection.point.x, imageWidth),
          y: toNorm(detection.point.y, imageHeight),
        },
      };
    }

    if (!annotation) continue; // no geometry — skip without consuming a number

    const existing = groups.get(detection.mention);
    if (existing) {
      existing.push(annotation);
    } else {
      groups.set(detection.mention, [annotation]);
    }
  }

  let collectionIndex = 0;
  return Array.from(groups.entries()).map(([mention, children]): CollectionAnnotation => {
    collectionIndex += 1;
    return {
      id: `content-collection-${collectionIndex}`,
      type: AnnotationType.COLLECTION,
      assetIdx: 0,
      label: mention,
      source: AnnotationSource.CONTENT,
      children,
    };
  });
}

/**
 * Run detection on the current image and categories. Requires an image;
 * categories are optional (the model returns its own when none are supplied).
 */
export async function runDetect(): Promise<void> {
  const detect = useDetectStore.getState();

  if (detect.status === "running") return;

  const { image } = detect;
  if (!image) {
    toast.error(strings.detect.errors.noImage);
    return;
  }

  // Commit any unsubmitted text in the category input so hitting Run doesn't drop it.
  const pending = detect.pendingCategory.trim();
  if (pending) {
    detect.addCategory(pending);
    detect.setPendingCategory("");
  }

  // Re-read so `categories` reflects the just-flushed pending entry.
  const { categories, enableThinking, outputType, setStatus, setRequestedCategories } = useDetectStore.getState();

  setStatus("running");
  // Snapshot the categories sent with this request so the UI can tell a category that was searched-for-but-empty from one added after the run.
  setRequestedCategories([...categories]);
  clearDetections();

  // Client-side run id tying this run's analytics events AND its server-side
  // save-detect-content record together (detect_demo_runs.run_uuid); never
  // forwarded to the gateway (matches the `model_run_*` convention).
  const runId = generateUUID();
  const startedAt = Date.now();
  trackDetect.start({
    run_id: runId,
    category_count: categories.length,
    exemplar_count: 0,
    output: outputType,
    enable_thinking: enableThinking,
    categories: [...categories],
  });

  const request: DetectRequest = {
    sessionUuid: useSessionStore.getState().sessionUuid,
    runUuid: runId,
    media: {
      type: "image",
      image_key: image.key,
      // Omitted when unknown: loadImageDimensionsFromUrl can yield 0 (e.g. an SVG
      // without intrinsic size) and the contract requires positive values.
      ...(image.width > 0 && image.height > 0 ? { width: image.width, height: image.height } : {}),
    },
    ...(categories.length > 0 ? { categories } : {}),
    config: { output: outputType, reasoning_effort: reasoningEffortForThinking(enableThinking) },
  };

  const controller = new AbortController();
  activeDetectAbort = controller;
  try {
    saveDetectRunPreferences({ outputType, enableThinking });
    const response = await demoDetectApi.detect(request, controller.signal);
    const annotations = detectionsToAnnotations(response.detections, image.width, image.height);
    useModelStore.getState().setAnnotations(annotations);
    setStatus("done");
    trackDetect.complete({
      run_id: runId,
      category_count: categories.length,
      exemplar_count: 0,
      output: outputType,
      enable_thinking: enableThinking,
      detection_count: response.detections.length,
      detected_category_count: new Set(response.detections.map((detection) => detection.mention)).size,
      finish_reason: response.finish_reason,
      duration_ms: Date.now() - startedAt,
    });
  } catch (error) {
    if (controller.signal.aborted) {
      // Stopped by the user; stopDetect already reset the status. The abort does
      // not propagate through the proxy, so the client is the only emitter of
      // the cancel (the gateway will still record its own terminal event).
      trackDetect.cancel({
        run_id: runId,
        time_before_cancellation_ms: Date.now() - startedAt,
        category_count: categories.length,
        output: outputType,
        enable_thinking: enableThinking,
      });
      return;
    }
    console.error("Detect request failed:", error);
    const message = error instanceof Error && error.message ? error.message : strings.detect.errors.requestFailed;
    toast.error(message);
    setStatus("error");
    trackDetect.fail({
      run_id: runId,
      error: message,
      status_code: errorStatusCode(error),
      time_before_failure_ms: Date.now() - startedAt,
      category_count: categories.length,
      output: outputType,
      enable_thinking: enableThinking,
    });
  } finally {
    if (activeDetectAbort === controller) activeDetectAbort = null;
  }
}

/** Abort the in-flight detect request (if any) and return the surface to idle. */
export function stopDetect(): void {
  activeDetectAbort?.abort();
  activeDetectAbort = null;
  useDetectStore.getState().setStatus("idle");
}
